import { Link } from 'react-router-dom'
import { useI18n } from '../i18n'

const featureIcons = ['📋', '🔔', '🔒', '📄', '🩺', '🤝']
const stepNumbers = ['01', '02', '03']
const heroImages = ['/horses/h02.jpg', '/horses/h03.jpg', '/horses/h00.jpg', '/horses/h05.jpg']

export function Landing() {
  const { t, tArr } = useI18n()
  const features = tArr('landing.features.items')
  const steps = tArr('landing.how.steps')
  const bullets = tArr('landing.privacy.bullets')

  return (
    <main>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute -right-40 -top-40 h-96 w-96 rounded-full bg-brand-200/50 blur-3xl dark:bg-brand-700/30" />
          <div className="absolute -left-32 top-40 h-80 w-80 rounded-full bg-brand-300/40 blur-3xl dark:bg-brand-800/40" />
        </div>
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 py-16 md:grid-cols-2 md:py-24">
          <div className="animate-fade-up">
            <span className="chip mb-5">{t('landing.heroBadge')}</span>
            <h1 className="text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl md:text-6xl">
              {t('landing.heroTitleA')}<br />
              <span className="text-brand-600 dark:text-brand-300">{t('landing.heroTitleB')}</span>
            </h1>
            <p className="mt-6 max-w-md text-lg text-brand-700 dark:text-brand-200">
              {t('landing.heroSubtitle')}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/signup" className="btn-primary">{t('landing.getStarted')}</Link>
              <Link to="/discover" className="btn-ghost">{t('landing.seePublic')}</Link>
            </div>
            <p className="mt-4 text-sm text-brand-500 dark:text-brand-400">{t('landing.trialNote')}</p>
          </div>

          {/* Hero collage */}
          <div className="animate-fade-up [animation-delay:120ms]">
            <div className="grid grid-cols-2 gap-4">
              {heroImages.map((src, i) => (
                <img
                  key={src}
                  src={src}
                  alt=""
                  className={`aspect-[3/4] w-full rounded-2xl object-cover shadow-lift ${i % 2 ? 'translate-y-6' : ''}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Two doors — horse owner vs farm owner */}
      <section className="mx-auto max-w-5xl px-5 py-6">
        <h2 className="mb-6 text-center text-2xl font-bold tracking-tight">{t('landing.twoDoor.title')}</h2>
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="card flex flex-col p-7">
            <div className="text-3xl">🐎</div>
            <h3 className="mt-3 text-xl font-bold">{t('landing.twoDoor.horseTitle')}</h3>
            <p className="mt-2 flex-1 text-sm leading-relaxed text-brand-700 dark:text-brand-200">{t('landing.twoDoor.horseBody')}</p>
            <Link to="/signup?role=horse" className="btn-primary mt-5 self-start">{t('landing.twoDoor.horseCta')}</Link>
          </div>
          <div className="card flex flex-col border-2 border-brand-300/70 dark:border-brand-600/60 p-7">
            <div className="text-3xl">🏡</div>
            <h3 className="mt-3 text-xl font-bold">{t('landing.twoDoor.farmTitle')}</h3>
            <p className="mt-2 flex-1 text-sm leading-relaxed text-brand-700 dark:text-brand-200">{t('landing.twoDoor.farmBody')}</p>
            <Link to="/signup?role=farm" className="btn-primary mt-5 self-start">{t('landing.twoDoor.farmCta')}</Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="mx-auto max-w-6xl px-5 py-16">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">{t('landing.features.title')}</h2>
          <p className="mt-4 text-brand-700 dark:text-brand-200">{t('landing.features.subtitle')}</p>
        </div>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((f, i) => (
            <div key={i} className="card p-6">
              <div className="grid h-12 w-12 place-items-center rounded-xl bg-brand-100 dark:bg-brand-800 text-2xl">{featureIcons[i]}</div>
              <h3 className="mt-4 text-lg font-bold">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-brand-700 dark:text-brand-200">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="bg-white/60 dark:bg-brand-800/30 py-16">
        <div className="mx-auto max-w-6xl px-5">
          <h2 className="text-center text-3xl font-bold tracking-tight sm:text-4xl">{t('landing.how.title')}</h2>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {steps.map((s, i) => (
              <div key={i} className="relative card p-7">
                <span className="text-4xl font-extrabold text-brand-200 dark:text-brand-700">{stepNumbers[i]}</span>
                <h3 className="mt-3 text-xl font-bold">{s.title}</h3>
                <p className="mt-2 text-brand-700 dark:text-brand-200">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Privacy */}
      <section id="privacy" className="mx-auto max-w-6xl px-5 py-16">
        <div className="card overflow-hidden md:grid md:grid-cols-2">
          <div className="p-8 md:p-12">
            <span className="chip mb-4">{t('landing.privacy.badge')}</span>
            <h2 className="text-3xl font-bold tracking-tight">{t('landing.privacy.title')}</h2>
            <p className="mt-4 text-brand-700 dark:text-brand-200">{t('landing.privacy.body')}</p>
            <ul className="mt-6 space-y-3 text-sm">
              {bullets.map((b: string) => (
                <li key={b} className="flex items-center gap-3">
                  <span className="grid h-6 w-6 place-items-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">✓</span>
                  {b}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-brand-100 dark:bg-brand-800 p-8 md:p-12">
            <img src="/horses/h03.jpg" alt="" className="h-full w-full rounded-2xl object-cover shadow-lift" />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-4xl px-5 pb-8 text-center">
        <div className="card bg-brand-700 px-8 py-14 text-white dark:bg-brand-700">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">{t('landing.cta.title')}</h2>
          <p className="mx-auto mt-4 max-w-lg text-brand-100">{t('landing.cta.body')}</p>
          <Link to="/signup" className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-brand-800 transition hover:bg-brand-50 active:scale-[0.98]">
            {t('landing.cta.button')}
          </Link>
        </div>
      </section>
    </main>
  )
}
